import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import { Box, Button, Tooltip } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import useIssueReport from '../../hooks/issueReport/useIssueReport.hooks';
import { selectCanFileIssueReport } from '../../redux/features/issueReport/issueReport.selectors';
import Colors from '../../utils/color';
import { HITL3_FONT_SIZE } from '../../utils/hitl3Typography';
import useFlagIssueHotkey from './useFlagIssueHotkey.hooks';
import IssueBacklogPopover from './IssueBacklogPopover';
import { ISSUE_TOOLTIP_Z_INDEX } from './issueReportLayers';

/**
 * How long the popover survives the pointer leaving it.
 *
 * The badge is 16px and the list opens above the button with a gap, so a bare
 * `mouseleave` close made it unreachable — the pointer cannot get from one to the
 * other without briefly being over neither. Long enough to cross the gap, short
 * enough that it does not linger over the ordering controls.
 */
export const BACKLOG_CLOSE_DELAY_MS = 150;

/**
 * HITL-1499: the affordance the whole issue-reporting pillar hangs on. One press
 * (or `Alt+I`) files a report joined to the live session, with no typing and no
 * detour off the ordering screen.
 *
 * Deliberately NOT an Intent: it lives in its own sibling group outside
 * `INTENT_SECTIONS`, so the `Alt+1…9` badge numbering — which derives from that
 * list — cannot renumber. Mis-fired ordering intents in a live drive-thru is the
 * risk being avoided.
 *
 * It has no COOLDOWN and does not fade under `isStaffInterventionHappening`, unlike
 * the intent pills: "Unnecessary Staff Intervention" is over half of all reports
 * today, so an intervention is the single moment reporting matters most. It is not a
 * TTS action either, so `intentsStatus` / `DISABLE_INTENT_INTERVAL` do not apply.
 *
 * It IS disabled for one reason and one only: a flag window is already open. That is
 * not a cooldown — it clears the moment the panel closes, which the hard cap
 * guarantees. The rule itself is enforced in the `flagIssue` thunk, because `Alt+I`
 * and the transcript source do not pass through this component; greying out here is
 * the VISIBLE half, so a blocked press reads as "not now" rather than as a dead
 * button.
 *
 * ## HITL-1574: the badge changed meaning
 *
 * It used to be an inline count of reports filed FOR THE CURRENT CAR, sitting inside
 * the button next to the label. It is now a corner badge counting OUTSTANDING work
 * across the shift, and hovering it opens the backlog.
 *
 * That is a different question, not a restyle. "Three filed for this car" was a fact
 * the agent could do nothing with, and it vanished when the car left — which is
 * exactly when the leftover work becomes doable. "Three still to classify" is
 * actionable and survives the car, so the number and the surface behind it are worth
 * the corner position.
 *
 * The button's own tooltip is back to being just the action hint. The session history
 * it used to carry is gone with the selectors that fed it — see
 * `selectOutstandingIssueReports` for the regression that names.
 */
const FlagIssueButton = () => {
  // `isEnabled` is selectIsIssueReportingEnabled — hitl_3_0 AND
  // hitl_issue_reporting, ANDed in one place. Do not re-AND it here.
  const { isEnabled, flagIssue, activeReport, outstandingReports } =
    useIssueReport();
  // The panel is up and owns the next 10s (up to 60s if the agent is typing a
  // detail — see MAX_FLAG_WINDOW_MS). Mirrors the thunk's guard exactly: both read
  // "is there an active report", so the button and the rule cannot disagree.
  const isFlagWindowOpen = Boolean(activeReport);
  /**
   * The second rule the thunk enforces, read from the same selector so this cannot
   * drift from it: a new report needs an active order interaction.
   *
   * This is the VISIBLE half only. `Alt+I` and the two transcript gestures never come
   * through this component, so the rule itself lives in the `flagIssue` thunk — see
   * `selectCanFileIssueReport` for what the predicate means and why it is not simply
   * `isCarPresent`.
   */
  const canFile = useSelector(selectCanFileIssueReport);
  const isDisabled = isFlagWindowOpen || !canFile;
  /**
   * Order matters: the open-window reason wins, because it is the actionable one and
   * the panel is on screen saying so.
   *
   * "No active order" rather than "No car at the lane", which would be a lie in the
   * one state an agent is most likely to read it: filing is still allowed while an
   * order tail is unsettled after the car has gone.
   */
  const hint = isFlagWindowOpen
    ? 'Finish the open report first'
    : !canFile
    ? 'No active order to report on'
    : 'Report an issue (Alt+I)';
  // The popover's anchor. Created for HITL-1500's cancelled quick-pick tray and
  // finally used here, which is why it needs no prop plumbing through
  // IntentsSectionGrouped.
  const buttonRef = useRef<HTMLButtonElement>(null);

  /**
   * Two independent reasons the backlog is open, deliberately not one enum.
   *
   * `isHovered` is transient and closes itself; `isLatched` is set by clicking a row
   * and survives the pointer leaving entirely. Collapsing them would mean a hover-out
   * could close a row the agent is typing in.
   */
  const [isHovered, setIsHovered] = useState(false);
  const [isLatched, setIsLatched] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>();

  const cancelClose = useCallback(() => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = undefined;
  }, []);

  const openBacklog = useCallback(() => {
    cancelClose();
    setIsHovered(true);
  }, [cancelClose]);

  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimerRef.current = setTimeout(
      () => setIsHovered(false),
      BACKLOG_CLOSE_DELAY_MS
    );
  }, [cancelClose]);

  const dismissBacklog = useCallback(() => {
    cancelClose();
    setIsHovered(false);
    setIsLatched(false);
  }, [cancelClose]);

  // A pending close must not fire into an unmounted component — the button unmounts
  // when the feature flag goes off mid-session.
  useEffect(() => cancelClose, [cancelClose]);

  /**
   * Closed while a flag window is open, matching the one-flag-at-a-time rule: the
   * panel is already classifying something, and a second classifier on screen at the
   * same time is two surfaces competing for the same 24-chip grid and the same caret.
   *
   * Also closed once the backlog empties, so classifying the last row does not leave
   * an empty box hanging over the action bar.
   *
   * DELIBERATELY NOT gated on `canFile`. Between cars the button is disabled and the
   * backlog is exactly what the agent should be working — that idle gap is the whole
   * reason HITL-1571 built these surfaces. Adding `&& canFile` here would hide the
   * outstanding work at the one moment it is most doable.
   */
  const hasOutstanding = outstandingReports.length > 0;
  const isBacklogOpen =
    (isHovered || isLatched) && hasOutstanding && !isFlagWindowOpen;

  // Called BEFORE the early return — hooks cannot be conditional. It is a no-op
  // with the flag off because `flagIssue` is already gated inside useIssueReport,
  // and a no-op with a window open because the thunk drops the press. The hotkey
  // deliberately learns NEITHER rule: see the note in useFlagIssueHotkey.
  useFlagIssueHotkey(() => flagIssue({ source: 'action_bar' }));

  if (!isEnabled) return null;

  // Staff's treatment, in Staff's exact geometry and typography — solid pill,
  // uppercase, letterSpacing, weight 700, 15px outlined icon. Reporting a defect
  // has to be findable instantly mid-order, and the escalation pill is the bar's
  // established shape for "this is the one control you may need in a hurry".
  //
  // The COLOUR is deliberately not Staff's. Two red pills side by side is a real
  // misclick hazard — a mis-hit on Staff escalates to the store for real — and the
  // amber / blue slots already mean Conversation and Order Flow. Daintree sits
  // outside that scheme entirely, so the button carries Staff's weight while
  // reading as its own thing.
  //
  // Styling is `sx`, not makeStyles: MUI Button's own emotion styles override
  // makeStyles classes (same reason IntentsSectionGrouped uses sx for its pills).
  const buttonSx = {
    height: 34,
    flex: '0 0 auto',
    minWidth: 64,
    px: 1.25,
    borderRadius: '4px',
    backgroundColor: Colors.daintree,
    color: Colors.white,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    fontWeight: 700,
    fontSize: HITL3_FONT_SIZE,
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
    '& .MuiButton-startIcon': { marginRight: '4px' },
    '&:hover': { backgroundColor: Colors.daintree },
    // MUI's default disabled treatment for a contained button is a light grey fill
    // with grey ink, which on this bar reads as BROKEN rather than as temporarily
    // unavailable — and it would drop the keycap's contrast below legible. Keep the
    // button's own colours and dim the whole thing instead, which carries the keycap
    // and the icon with it.
    '&.Mui-disabled': {
      backgroundColor: Colors.daintree,
      color: Colors.white,
      opacity: 0.45,
    },
  };

  /**
   * The outstanding-work badge: a number, on the button's top-right corner.
   *
   * OUTSIDE the Tooltip's child, and that placement is the whole arbitration between
   * two hover surfaces on one control. HITL-1501 found that a tooltip on a child
   * fires the parent's too, because `mouseover` bubbles, and the agent got both at
   * once. A sibling never reaches the button's hover listener at all, so there is
   * nothing to arbitrate.
   *
   * RED, not the old inline badge's translucent white and not the green it shipped
   * with: it is now a call to action rather than a running total, and green reads as
   * "everything is fine" over a count of work nobody has done yet.
   *
   * `Colors.amaranth` specifically, which is this console's existing ERROR tone — the
   * Order Status Banner's error ring (HITL-1527). Deliberately neither of the other
   * two reds available: `Colors.requestStaff` is the intent bar's escalation red and
   * means "get a human to the lane", which this does not, and `Colors.red` is pure
   * `#FF0000` (see the note in IssueFlagPanel).
   *
   * The reason the BUTTON is not red does not apply here. That was a misclick hazard
   * next to Staff — and this badge has no click handler at all, so there is nothing
   * to mis-click. It opens on hover and the rows are what get pressed.
   *
   * White ink on amaranth is ~4.1:1, under AA for 11px bold. No saturated red clears
   * 4.5:1, and the count is carried losslessly by `aria-label` for anyone who cannot
   * read it; a muted red that did pass would blend into the daintree button it sits
   * on, which costs the badge the one job it has.
   */
  const badgeSx = {
    position: 'absolute',
    top: -6,
    right: -6,
    zIndex: 1,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 18,
    height: 18,
    px: '5px',
    borderRadius: '9px',
    backgroundColor: Colors.amaranth,
    color: Colors.white,
    fontSize: '0.6875rem',
    fontWeight: 700,
    lineHeight: 1,
    letterSpacing: 0,
    fontVariantNumeric: 'tabular-nums',
    cursor: 'pointer',
    // The badge straddles the dark button and the white bar, so it needs a rim of its
    // own or its edge dissolves into whichever half it overlaps.
    //
    // `mexicanRed` is the palette's DARK red — the Order Status Banner's error ink,
    // the counterpart to the amaranth fill. A rim in the fill's own red would not be
    // a rim at all: it would just make the badge 4px wider with a soft edge against
    // the white bar, which is the one place the rim is doing work.
    boxShadow: `0 0 0 2px ${Colors.mexicanRed}`,
  };

  const keycapSx = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 15,
    height: 15,
    px: '3px',
    mr: 0.5,
    borderRadius: '3px',
    border: `1px solid ${Colors.white}`,
    backgroundColor: Colors.white,
    color: Colors.daintree,
    fontSize: '0.62rem',
    fontWeight: 700,
    lineHeight: 1,
  };

  return (
    // The group wrapper lives HERE, not in IntentsSectionGrouped, so that with the
    // flag off this component emits nothing at all — an always-rendered empty
    // wrapper would add one more flex child and one more 8px gap to the bar on
    // every store that has the feature off.
    //
    // data-issue-report on this outermost node is what opts the whole control out
    // of agent-interception detection: the guards listen on document in the
    // capture phase and match with target.closest(), so the marker has to be an
    // ANCESTOR of whatever the real mousedown target is — the svg icon, the keycap
    // Box or the badge, not the button. Repeated on the Button itself as belt and
    // braces. Flagging a defect is supervisory, not taking the order over.
    //
    // `position: relative` is what the corner badge is positioned against.
    <Box
      data-issue-report="true"
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        flex: '0 0 auto',
      }}
    >
      {/* The tooltip has to change with the state: "Report an issue" on a button
          that cannot report one is a lie, and the disabled tooltip is the only place
          the reason is written down. The <span> wrapper is what makes a tooltip on a
          DISABLED button work at all — MUI cannot listen on the button itself once
          it stops firing pointer events.

          Back to just the hint. It used to also carry this session's flag history,
          which went with the session-scoped selectors in HITL-1574.

          `disableInteractive` stays: this floats over the action bar, and an
          interactive tooltip there would block the ordering controls underneath it.
          The backlog is a Popper precisely because it needs the opposite. */}
      <Tooltip
        title={hint}
        // Belt and braces on top of the badge being a sibling: if the pointer does
        // reach the button on its way to the list, the hint must not open over it.
        disableHoverListener={isBacklogOpen}
        componentsProps={{
          // Same layering rule as the panel — see issueReportLayers. `as never`: MUI
          // types this slot as Partial<PopperProps>, which has no `sx`, though the
          // styled Popper it reaches does honour it.
          popper: { sx: { zIndex: ISSUE_TOOLTIP_Z_INDEX } } as never,
          tooltip: { sx: { maxWidth: 420 } },
        }}
        arrow
        disableInteractive
      >
        <span>
          <Button
            ref={buttonRef}
            onClick={() => flagIssue({ source: 'action_bar' })}
            disabled={isDisabled}
            disableElevation
            data-issue-report="true"
            startIcon={<FlagOutlinedIcon sx={{ fontSize: 15 }} />}
            sx={buttonSx}
          >
            <Box component="span" sx={keycapSx}>
              I
            </Box>
            Issue
          </Button>
        </span>
      </Tooltip>

      {/* Rendered only from 1 up. That absence is the whole zero state: nothing
          outstanding means nothing to say, and no empty badge to design.

          Hover-only, no click handler. Clicking is what the ROWS are for, and a
          badge that both opened on hover and toggled on click would fight its own
          click-away dismissal. */}
      {hasOutstanding && (
        <Box
          component="span"
          data-testid="issue-outstanding-count"
          aria-label={`${outstandingReports.length} issues to classify`}
          onMouseEnter={openBacklog}
          onMouseLeave={scheduleClose}
          sx={badgeSx}
        >
          {outstandingReports.length}
        </Box>
      )}

      <IssueBacklogPopover
        anchorEl={buttonRef.current}
        open={isBacklogOpen}
        reports={outstandingReports}
        onPointerEnter={openBacklog}
        onPointerLeave={scheduleClose}
        onDismiss={dismissBacklog}
        onLatch={() => setIsLatched(true)}
      />
    </Box>
  );
};

export default FlagIssueButton;
